import { Link } from 'react-router-dom';
import {
  V2Button,
  V2Card,
  V2CardHeader,
  Icon,
  type SakhtarIconName,
} from '@sakhtar/design-system/react';

interface QuickReport {
  readonly id: string;
  readonly label: string;
  readonly icon: SakhtarIconName;
  /** Relative to `/t/:slug/finance`. */
  readonly href: string;
}

/** Every entry is an existing routed report page (App.tsx `finance/reports/*`). */
const QUICK_REPORTS: readonly QuickReport[] = [
  { id: 'trial-balance', label: 'تراز آزمایشی', icon: 'finance', href: '/reports/trial-balance' },
  { id: 'general-ledger', label: 'گردش حساب', icon: 'trend', href: '/reports/ledger?mode=general' },
  {
    id: 'subsidiary-ledger',
    label: 'مانده حساب',
    icon: 'layers',
    href: '/reports/ledger?mode=subsidiary',
  },
];

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/finance`,
 *   overview tab, «گزارش‌های آماده» card
 * canonical pattern: tinted V2CardHeader + icon-chip rows with a ghost «نمایش»
 * status: PARITY — the three rows of the frame (تراز آزمایشی / گردش حساب /
 *   مانده حساب) link to real report pages: trial balance
 *   (`GET .../finance/reports/trial-balance`), and the ledger report in
 *   general / subsidiary mode (`GET .../finance/reports/ledger`, LedgerPage).
 *   The frame wires only the first row; the other two are routed here because
 *   the ledger pages exist. Static links — no data, no fabricated figure.
 */
export function OverviewQuickReports({ base }: { readonly base: string }) {
  return (
    <V2Card>
      <V2CardHeader title="گزارش‌های آماده" icon="layers" />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {QUICK_REPORTS.map((report, index) => (
          <div
            key={report.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 14px',
              borderBottom:
                index < QUICK_REPORTS.length - 1 ? '1px solid var(--sk-divider)' : undefined,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                flex: '0 0 28px',
                borderRadius: 8,
                display: 'grid',
                placeContent: 'center',
                color: 'color-mix(in oklch, var(--sk-mod-c, var(--sk-success)) 60%, var(--sk-fg))',
                background:
                  'color-mix(in oklch, var(--sk-mod-c, var(--sk-success)) 12%, var(--sk-bg-elev))',
              }}
            >
              <Icon name={report.icon} size="sm" />
            </span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{report.label}</span>
            <Link to={`${base}/finance${report.href}`}>
              <V2Button variant="ghost" size="sm">
                نمایش
              </V2Button>
            </Link>
          </div>
        ))}
      </div>
    </V2Card>
  );
}
